import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { BUTTON_TYPES, Button, Checkbox, Image } from '@src/quince-components/src'
import { renderProductOption } from '@src/quince-components/src/atoms/productForm/ProductForm'
import SiblingProductOptions, { SiblingProductOptionType } from '@src/quince-components/src/atoms/siblingProductOptions'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { Fragment, useMemo } from 'react'

import styles from './BundleProductCard.module.scss'
const OPTION_DISPLAY_TYPES_VS_CLASS_NAME = {
  [ProductOptionDisplayType.TEXT]: styles.textOptionLabelClassname,
}

export interface BundleProductCardProps {
  product: Product
  selectedOptions: ProductVariantOption[]
  salePrice: string
  currency: string
  discount: number
  isAddOnProduct?: boolean
  expanded?: boolean
  selectedSiblingProductId?: number
  error?: string | null
  onSiblingOptionSelect?: (val: string) => void
  setExpanded?: (expanded: boolean) => void
  onOptionSelect: (optionName: string, optionValue: string) => void
  showSizeChart?: () => void
}

function BundleProductCard({
  product,
  selectedOptions,
  salePrice,
  currency,
  discount,
  isAddOnProduct = false,
  expanded = true,
  selectedSiblingProductId,
  error = null,
  onSiblingOptionSelect,
  setExpanded,
  onOptionSelect,
  showSizeChart,
}: BundleProductCardProps) {
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('pdp')
  const { t: tCommon } = useTranslation('common')
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const discountedSalePrice = useMemo(() => {
    return getDiscountedPrice(salePrice, discount)
  }, [selectedOptions])

  const productOptions = productHelper.getCascadingProductOptions(product, selectedOptions)
  const optionNameSelectedValueMap = selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const chooseProductText = isAddOnProduct ? `Add a ${product.title}` : product.title

  const productMedia = useMemo(
    () => productHelper.getImagesForOptions(product, selectedOptions),
    [product, selectedOptions],
  )

  const renderBundleProductDetailsSection = () => {
    const productOptionsRenderer = productOptions
      .filter(po => !po.isHidden)
      .map(productOption => ({
        node: renderProductOption({
          productOption: productOption,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          onOptionSelect: onOptionSelect,
          hideSizeChart: true,
          sizeChart: undefined,
          handleSizeChart: EMPTY_ARROW_FUNC,
          product: product,
          selectedOptions: selectedOptions,
          optionsContainerClassName: styles.productOptions,
          mergedSizeChart: undefined,
          legendClassName: styles.productOptionsLegend,
          optionSelectorClassName: styles.productOptionsSelector,
          optionDisplayTypesVsClassName: OPTION_DISPLAY_TYPES_VS_CLASS_NAME,
          t: tCommon,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }))

    const addOnOptionsRenderer = product.addOnProductOptionGroups?.map(group => {
      return {
        node: (
          <SiblingProductOptions
            siblingProductOptionGroup={group}
            displayType={onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
            selectedProductId={selectedSiblingProductId}
            onSelect={onSiblingOptionSelect}
            optionSelectorClassName={styles.siblingOptionsSelector}
            className={styles.siblingOptions}
          />
        ),
        displaySequence: group.optionDisplaySequence,
        key: group.groupId,
      }
    })

    const combinedProductOptions = [...(addOnOptionsRenderer || []), ...productOptionsRenderer]
    combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
    return (
      expanded && (
        <>
          <div className={styles.productOptionsContainer}>
            {combinedProductOptions.map(option => (
              <Fragment key={option.key}>{option.node}</Fragment>
            ))}
          </div>
          {(product.sizeChart || product.mergedSizeChart) && showSizeChart && (
            <div className={styles.sizeChart}>
              <Button type={BUTTON_TYPES.SECONDARY_LINK} onClick={showSizeChart} className={styles.sizeChart}>
                {t('size_chart')}
              </Button>
            </div>
          )}
          {error && <div className={styles.errorMessage}>{error}</div>}
        </>
      )
    )
  }

  return (
    <>
      <div className={classNames(styles.bundleCardWrapper, isAddOnProduct && styles.addOnBundle)}>
        <div className={styles.bundleCard}>
          <div
            className={classNames(
              styles.imageContainer,
              isAddOnProduct && (isMobile || !expanded) && styles.addOnProductImageContainer,
            )}
          >
            <Image
              url={productMedia.length ? (productMedia[0]?.image.url ?? '') : ''}
              title={product.title}
              maxWidth={250}
              overrideMaxImageWidth1600
              alt={product.title}
            />
          </div>
          <div className={styles.productDetails}>
            <div className={styles.titleAndPriceSection}>
              <div>
                <div className={styles.checkboxAndTitle}>
                  {isAddOnProduct && (
                    <div className={styles.checkboxWrapper}>
                      {/* eslint-disable-next-line */}
                      <Checkbox isChecked={expanded} setIsChecked={checked => setExpanded && setExpanded(checked)} />
                    </div>
                  )}
                  {chooseProductText}
                </div>
              </div>
              <div className={styles.pricing}>
                <div data-testid={TEST_LOCATORS.BUNDLE_PRODUCT_SALE_PRICE} className={styles.salePrice}>
                  {currency}
                  {discountedSalePrice}
                </div>
                <div className={styles.salePriceBeforeBundleDiscount}>
                  {currency}
                  {salePrice}
                </div>
              </div>
            </div>
            {!isMobile && renderBundleProductDetailsSection()}
          </div>
        </div>
        {isMobile && renderBundleProductDetailsSection()}
      </div>
    </>
  )
}

export default BundleProductCard
